import { ref, onMounted, onUnmounted } from 'vue';
import { httpService } from '../../services/httpService';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';

import '../../contracts/ProducaoKpi_CardsResponse';
import '../../contracts/ProducaoKpi_ServicosMensalResponse';

import type { ProducaoKpi_CardsResponseDados } from '../../contracts/ProducaoKpi_CardsResponse';
import type { ProducaoKpi_ServicosMensalMesRow } from '../../contracts/ProducaoKpi_ServicosMensalMesRow';

const BASE = '/index.php/dashboards/producao_kpi';

export function useDashboardProducaoKpi() {
  const cards = ref<ProducaoKpi_CardsResponseDados | null>(null);
  const servicosMensal = ref<ProducaoKpi_ServicosMensalMesRow[] | null>(null);

  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const fetchCards = async (): Promise<void> => {
    log('useDashboardProducaoKpi', 'fetchCards', 'GET', `${BASE}/cards`);
    const response = await httpService.get(`${BASE}/cards`, {
      responseContract: 'ProducaoKpi_CardsResponse',
    });
    if (!response.success) {
      throw new Error(response.error || 'Erro ao buscar cards de producao');
    }
    cards.value = response.dados ?? null;
  };

  const fetchServicosMensal = async (): Promise<void> => {
    log('useDashboardProducaoKpi', 'fetchServicosMensal', 'GET', `${BASE}/servicos_mensal`);
    const response = await httpService.get(`${BASE}/servicos_mensal`, {
      responseContract: 'ProducaoKpi_ServicosMensalResponse',
    });
    if (!response.success) {
      throw new Error(response.error || 'Erro ao buscar servicos mensais');
    }
    servicosMensal.value = response.dados?.meses ?? [];
  };

  const fetchAll = async (): Promise<void> => {
    isLoading.value = true;
    error.value = null;
    try {
      await Promise.all([fetchCards(), fetchServicosMensal()]);
      eventBus.emit('dashboard.producao_kpi.loaded');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      log('useDashboardProducaoKpi', 'fetchAll', 'Error', message);
      error.value = message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const onFilterUpdated = (): void => {
    log('useDashboardProducaoKpi', 'onFilterUpdated', 'Refetching');
    fetchAll().catch((err) => {
      log('useDashboardProducaoKpi', 'onFilterUpdated', 'Refetch failed', err);
    });
  };

  onMounted(() => {
    eventBus.on('filter.updated', onFilterUpdated);
    fetchAll().catch((err) => {
      log('useDashboardProducaoKpi', 'onMounted', 'Initial fetch failed', err);
    });
  });

  onUnmounted(() => {
    eventBus.off('filter.updated', onFilterUpdated);
  });

  return {
    cards,
    servicosMensal,
    isLoading,
    error,
    fetchCards,
    fetchServicosMensal,
    fetchAll,
  };
}
